import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import ScheduleView from '../components/ScheduleView'
import {
  EditIcon,
  CalendarIcon,
  ClockIcon,
  FileTextIcon,
  ChevronDownIcon,
  SaveIcon,
  XIcon,
  RefreshCwIcon,
  BriefcaseIcon,
  ClipboardListIcon,
  MoreHorizontalIcon,
  BuildingIcon,
  DollarSignIcon,
  StickyNoteIcon,
  HistoryIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { MobilePage, MobilePageHeader, MobileSheet, mobile } from '../components/mobile'
import { ViewTabBar, ViewSection, ViewStatCard, ViewEmptyState } from '../components/ViewPageLayout'
import { ContractLineItemQrRequirements } from '../components/contract/ContractLineItemQrRequirements'
import { ConversationsPanel } from '../components/ConversationsPanel'
import { logActivity, getEntityActivities, ActivityLog, formatActivityMeta } from '../utils/activityLogger'
import { ContractJobsCalendar } from '../components/ContractJobsCalendar'
import { JobTemplateEditor } from '../components/JobTemplateEditor'
import { ContractJobTemplate, deleteJobTemplate } from '../api/contractJobTemplates'
import {
  VisitGroupEditor,
  ContractVisitGroup as SharedContractVisitGroup,
  describeVisitGroupCadence,
} from '../components/VisitGroupEditor'
import { VisitScheduleBoard } from '../components/VisitScheduleBoard'
import {
  getGroupAssignments,
  normalizeVisitGroupFromDb,
  describeContractServiceSchedule,
} from '../lib/visitGroupBoardOps'
import {
  replaceContractVisitGroups,
  buildDsnyVisitBoardServices,
  hydrateDsnyScheduleRowsFromContractServices,
  ensureDsnyVisitGroupsForDisplay,
  syncVisitGroupAssigneesFromScheduleRows,
  preserveVisitGroupTargetAssignees,
  reconcileLoadedDsnyVisitGroups,
  hasMisplacedPiggybackServices,
  healMisplacedPiggybackAssignments,
} from '../lib/dsnyContractPersistence'
import { resolveDsnyCombineTarget } from '../lib/contractServiceLineItems'
import { useVisitGroupTemplates } from '../hooks/useVisitGroupTemplates'
import { getServiceTypeLabel } from '../lib/serviceTypeOptions'
import { contractToDocumentProps } from '../lib/documentProps'
import { downloadDocumentPdf } from '../lib/downloadDocumentPdf'
import { fetchContractMilestones } from '../lib/milestoneBilling'
import { getContractStatusColor, formatContractStatusLabel } from '../lib/contractStatusWorkflow'
import { showBillingDayPicker } from '../lib/billingPeriods'
import { formatBillingDayOfMonth } from '../components/BillingDayOfMonthSelect'
import type { BillingMilestone } from '../types/agreement'
import { formatContractTypeLabel } from '../types/agreement'
import {
  fetchContractProperties,
  type ContractPropertyLink,
} from '../api/turnoverContracts'
import { fetchCrewsAndEmployees, type CrewOption, type EmployeeOption } from '../api/jobWorkflow'
import {
  ContractAssignmentSummary,
  formatVisitGroupAssigneeLabel,
} from '../components/contract/ContractAssignmentSummary'
import type { AssignmentSource } from '../api/jobAssignmentDefaults'

interface Contract {
  id: string
  contract_number: string
  title: string
  description?: string
  template_type?: string
  contract_type: 'one_time' | 'recurring' | 'as_needed'
  service_type?: string
  total_amount?: number
  contract_total_amount?: number
  billing_frequency?: string
  billing_day_of_month?: number
  billing_method?: 'per_period' | 'divide_total' | 'actual_usage' | 'progress'
  sales_tax_status?: string
  is_recurring: boolean
  recurrence_type?: string
  recurrence_days?: number[]
  dsny_integration?: boolean
  dsny_pickup_days?: string[]
  dsny_collection_types?: string[]
  interior_cleaning_schedule?: string[]
  // Comprehensive schedule system
  master_weekly_schedule?: number[]
  garbage_schedule?: number[]
  recycling_schedule?: number[]
  organics_schedule?: number[]
  bulk_schedule?: number[]
  // Dynamic manual schedules
  manual_schedules?: Array<{
    id: string
    name: string
    description: string
    color: string
    days: number[]
  }>
  status:
    | 'draft'
    | 'pending_signature'
    | 'signed'
    | 'in_process'
    | 'active'
    | 'paused'
    | 'completed'
    | 'cancelled'
    | 'expired'
  start_date?: string
  end_date?: string
  signed_date?: string
  parent_contract_id?: string
  renewal_date?: string
  original_contract_id?: string
  payment_terms?: string
  late_fee_percentage?: number
  cancellation_terms?: string
  notes?: string | Array<{ timestamp: string; note: string }>
  created_at: string
  updated_at: string
  customer?: {
    id: string
    company_name?: string
    contact_first_name: string
    contact_last_name: string
    email?: string
    phone?: string
  }
  property?: {
    id?: string
    name: string
    address: string
    city?: string
    state?: string
    zip_code?: string
  }
  proposal_id?: string
  proposal?: {
    id?: string
    title: string
    proposal_number: string
  }
  assignment_source?: AssignmentSource | string
  default_assigned_crew_id?: string | null
  default_assigned_employee_id?: string | null
  property_id?: string | null
}

interface ContractVisitGroup {
  id: string
  contract_id: string
  name: string
  recurrence_type: string
  recurrence_days: number[]
  start_date?: string | null
  end_date?: string | null
  scheduled_start_time?: string | null
  scheduled_end_time?: string | null
  billing_driver?: string | null
  dsny_integration?: boolean
  dsny_pickup_days?: string[]
  is_active?: boolean
  assigned_crew_id?: string | null
  assigned_employee_id?: string | null
  checklist_items?: Array<{ label: string; sort_order: number; photo_required?: boolean }>
  checklist_overrides?: Record<
    string,
    Array<{ label: string; sort_order: number; photo_required?: boolean }>
  >
  service_ids: string[]
}

export function ContractDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const [contract, setContract] = useState<Contract | null>(null)
  const [contractProperties, setContractProperties] = useState<ContractPropertyLink[]>([])
  const [loading, setLoading] = useState(true)
  const [contractServices, setContractServices] = useState<any[]>([])
  const [lineItems, setLineItems] = useState<any[]>([])
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState<
    'overview' | 'services' | 'billing' | 'notes' | 'jobs'
  >(
    'overview'
  )
  const [showVisitBoard, setShowVisitBoard] = useState(false)
  const [contractJobs, setContractJobs] = useState<any[]>([])
  const [billingMilestones, setBillingMilestones] = useState<BillingMilestone[]>([])
  const [jobTemplates, setJobTemplates] = useState<ContractJobTemplate[]>([])
  const [jobsViewMode, setJobsViewMode] = useState<'calendar' | 'list'>('calendar')
  const [loadingJobs, setLoadingJobs] = useState(false)
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<ContractJobTemplate | null>(null)
  const [newTemplateDate, setNewTemplateDate] = useState<string | undefined>(undefined)
  const [notes, setNotes] = useState<Array<{ timestamp: string; note: string }>>([])
  const [newNote, setNewNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [activities, setActivities] = useState<ActivityLog[]>([])
  const [parentContract, setParentContract] = useState<Contract | null>(null)
  const [renewalContracts, setRenewalContracts] = useState<Contract[]>([])
  const [invoices, setInvoices] = useState<any[]>([])
  const [visitGroups, setVisitGroups] = useState<ContractVisitGroup[]>([])
  const [visitGroupsLoaded, setVisitGroupsLoaded] = useState(false)
  const [crews, setCrews] = useState<CrewOption[]>([])
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [propertyAssignmentLabels, setPropertyAssignmentLabels] = useState<{
    crewName: string | null
    employeeName: string | null
  } | null>(null)
  const [savingVisitGroups, setSavingVisitGroups] = useState(false)
  const [serviceItemCatalog, setServiceItemCatalog] = useState<
    Array<{ id: string; name: string; default_checklist?: unknown }>
  >([])
  const [advancedVisitGroupId, setAdvancedVisitGroupId] = useState<string | null>(null)
  const [projectedRecurringDates, setProjectedRecurringDates] = useState<string[]>([])
  const [generatingPdf, setGeneratingPdf] = useState(false)
  const [actionsOpen, setActionsOpen] = useState(false)
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error' | 'info'
  } | null>(null)
  const [generateJobsOpen, setGenerateJobsOpen] = useState(false)
  const [generateJobsStart, setGenerateJobsStart] = useState('')
  const [generateJobsEnd, setGenerateJobsEnd] = useState('')
  const [generateJobsPreview, setGenerateJobsPreview] = useState<{
    count: number
    summary: string
  } | null>(null)
  const [generateJobsBusy, setGenerateJobsBusy] = useState(false)
  const contractServicesRef = useRef<any[]>([])

  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'info' = 'info',
    ms = 4000
  ) => {
    setNotification({ message, type })
    window.setTimeout(() => setNotification(null), ms)
  }

  const openGenerateJobsModal = () => {
    const today = new Date().toISOString().split('T')[0]
    const defaultEnd = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    setGenerateJobsStart(today)
    setGenerateJobsEnd(defaultEnd)
    setGenerateJobsPreview(null)
    setGenerateJobsOpen(true)
  }

  const runGenerateJobsPreview = async () => {
    if (!id) return
    const startDate = new Date(generateJobsStart)
    const endDate = new Date(generateJobsEnd)
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      showNotification('Enter valid start and end dates.', 'error')
      return
    }
    if (endDate < startDate) {
      showNotification('End date must be on or after the start date.', 'error')
      return
    }

    try {
      setGenerateJobsBusy(true)
      const { previewJobsForContractRange } = await import('../api/jobGeneration')
      const preview = await previewJobsForContractRange(id, startDate, endDate)
      const groupedPreview = preview.reduce((acc: Record<string, number>, row) => {
        const key = row.visit_group_name || 'Visit Group'
        acc[key] = (acc[key] || 0) + 1
        return acc
      }, {})
      const previewSummary = Object.entries(groupedPreview)
        .map(([name, count]) => `${name}: ${count}`)
        .join(' · ')
      setGenerateJobsPreview({
        count: preview.length,
        summary: previewSummary || 'No visit groups in range',
      })
    } catch (error) {
      console.error('Error previewing jobs:', error)
      showNotification('Error previewing jobs: ' + (error as any).message, 'error')
    } finally {
      setGenerateJobsBusy(false)
    }
  }

  const confirmGenerateJobs = async () => {
    if (!id) return
    const startDate = new Date(generateJobsStart)
    const endDate = new Date(generateJobsEnd)
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      showNotification('Enter valid start and end dates.', 'error')
      return
    }

    try {
      setGenerateJobsBusy(true)
      setLoadingJobs(true)
      const { generateJobsForContract } = await import('../api/jobGeneration')
      const created = await generateJobsForContract(id, startDate, endDate)
      await fetchContractJobs(id)
      await fetchJobTemplates(id)
      setGenerateJobsOpen(false)
      setGenerateJobsPreview(null)
      showNotification(
        `Jobs generated successfully. Created ${created.length} job(s).`,
        'success',
        5000
      )
    } catch (error) {
      console.error('Error generating jobs:', error)
      showNotification('Error generating jobs: ' + (error as any).message, 'error', 6000)
    } finally {
      setGenerateJobsBusy(false)
      setLoadingJobs(false)
    }
  }

  const isDsnyMaintenance = useMemo(
    () => contractServices.some((s) => s.dsny_integration),
    [contractServices]
  )

  const dsnyScheduleRows = useMemo(() => {
    if (!isDsnyMaintenance) return []
    const mappings = visitGroups.flatMap((g) =>
      getGroupAssignments(g as unknown as SharedContractVisitGroup).map((a) => ({
        contract_service_id: a.service_id,
        schedule_mode: a.schedule_mode,
        recurrence_type: a.recurrence_type,
        recurrence_weekday: a.recurrence_weekday,
        recurrence_occurrence: a.recurrence_occurrence,
        recurrence_day_of_month: a.recurrence_day_of_month,
        recurrence_nearest_visit: a.recurrence_nearest_visit,
        visit_group_name: g.name,
        visit_group_dsny_integration: !!(g as { dsny_integration?: boolean }).dsny_integration,
      }))
    )
    const rows = hydrateDsnyScheduleRowsFromContractServices(contractServices, mappings)
    // Assignees live on visit groups, not contract_services — copy onto rows so
    // Exterior/Interior summary and any schedule UI stay aligned with the DB.
    return rows.map((row) => {
      const combineTarget = resolveDsnyCombineTarget(row as any)
      let group =
        combineTarget === 'dsny'
          ? visitGroups.find((g) => g.dsny_integration && (g.assigned_crew_id || g.assigned_employee_id)) ||
            visitGroups.find((g) => g.dsny_integration)
          : combineTarget === 'hallway'
            ? visitGroups.find(
                (g) =>
                  /^Hallway Cleaning\b/i.test(g.name || '') &&
                  (g.assigned_crew_id || g.assigned_employee_id)
              ) || visitGroups.find((g) => /^Hallway Cleaning\b/i.test(g.name || ''))
            : undefined
      if (!group) return row
      return {
        ...row,
        assigned_crew_id: group.assigned_crew_id || row.assigned_crew_id || null,
        assigned_employee_id: group.assigned_employee_id || row.assigned_employee_id || null,
      }
    })
  }, [contractServices, visitGroups, isDsnyMaintenance])

  const visitBoardServices = useMemo(() => {
    const catalogById = new Map(serviceItemCatalog.map((i) => [i.id, i]))
    const catalogByName = new Map(
      serviceItemCatalog.map((i) => [String(i.name || '').trim().toLowerCase(), i])
    )
    const attachCatalog = (s: {
      id: string
      service_name?: string
      name?: string
      service_item_id?: string
      [key: string]: unknown
    }) => {
      const fromId = s.service_item_id ? catalogById.get(s.service_item_id) : null
      const fromName = catalogByName.get(String(s.service_name || s.name || '').trim().toLowerCase())
      const catalog = fromId || fromName
      return {
        ...s,
        service_item_id: s.service_item_id || catalog?.id,
        default_checklist: Array.isArray(catalog?.default_checklist)
          ? catalog!.default_checklist
          : [],
      }
    }

    if (!isDsnyMaintenance) {
      return contractServices.map((s) =>
        attachCatalog({
          id: s.id,
          service_name: s.service_name,
          name: s.service_name,
          service_item_id: s.service_item_id,
        })
      )
    }
    const packageName =
      contractServices.find((s) => s.dsny_integration)?.service_name || 'Building Upkeep'
    return buildDsnyVisitBoardServices(dsnyScheduleRows, contractServices, packageName).map(
      (s) => {
        const cs = contractServices.find((c) => c.id === s.id)
        return attachCatalog({
          ...s,
          service_item_id: cs?.service_item_id,
        })
      }
    )
  }, [contractServices, dsnyScheduleRows, isDsnyMaintenance, serviceItemCatalog])

  // Heal loaded groups only — never full ensure/re-derive here. ensure([], services)
  // invents blank-assignee groups; depending on dsnyScheduleRows (derived from
  // visitGroups) also created a circular overwrite race after DB load.
  useEffect(() => {
    if (!isDsnyMaintenance || !visitGroupsLoaded || contractServices.length === 0) return

    setVisitGroups((prev) => {
      if (prev.length === 0) return prev
      const reconciled = reconcileLoadedDsnyVisitGroups(
        prev as unknown as SharedContractVisitGroup[],
        contractServices,
        { scheduleRows: dsnyScheduleRows as any }
      )
      const packageLabel =
        contractServices.find((s: any) => s.dsny_integration)?.service_name || 'Building Upkeep'
      const board = buildDsnyVisitBoardServices(
        dsnyScheduleRows as any,
        contractServices,
        packageLabel
      )
      const healed = hasMisplacedPiggybackServices(reconciled, board)
        ? healMisplacedPiggybackAssignments(reconciled, board)
        : reconciled
      const fixed = preserveVisitGroupTargetAssignees(
        prev as unknown as SharedContractVisitGroup[],
        healed
      )
      return JSON.stringify(fixed) === JSON.stringify(prev) ? prev : (fixed as ContractVisitGroup[])
    })
    // Intentionally omits visitGroups from deps so manual board edits are not overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDsnyMaintenance, visitGroupsLoaded, contractServices, id])

  const handleGeneratePdf = async () => {
    if (!contract) return
    setGeneratingPdf(true)
    try {
      const props = contractToDocumentProps(contract, {
        customer: (contract as any).customer,
        lead: null,
        property: (contract as any).property,
        project: null,
        lineItems,
      })
      const fileName = `Contract-${contract.contract_number || contract.id}`
      await downloadDocumentPdf(props, fileName)
    } catch (err) {
      console.error('Error generating PDF:', err)
      alert('Could not generate the PDF: ' + (err as any)?.message)
    } finally {
      setGeneratingPdf(false)
    }
  }
  const { templates: vgTemplates, saveGroupAsTemplate } = useVisitGroupTemplates()

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setVisitGroupsLoaded(false)
    ;(async () => {
      fetchContract(id)
      fetchContractLineItems(id)
      fetchContractJobs(id)
      fetchRenewalHistory(id)
      fetchInvoices(id)
      // Services before visit groups — ensure() needs the package row.
      await fetchContractServices(id)
      if (!cancelled) await fetchVisitGroups(id)
    })()
    return () => {
      cancelled = true
    }
  }, [id])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data, error } = await supabase
        .from('service_items')
        .select('id, name, default_checklist')
      if (cancelled || error) return
      setServiceItemCatalog((data || []) as Array<{ id: string; name: string; default_checklist?: unknown }>)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    fetchCrewsAndEmployees().then(({ crews: c, employees: e }) => {
      setCrews(c)
      setEmployees(e)
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!contract?.property_id) {
        setPropertyAssignmentLabels(null)
        return
      }
      const { data: prop } = await supabase
        .from('properties')
        .select('default_assigned_crew_id, default_assigned_employee_id')
        .eq('id', contract.property_id)
        .maybeSingle()
      if (cancelled || !prop) {
        if (!cancelled) setPropertyAssignmentLabels(null)
        return
      }
      const [crewRes, empRes] = await Promise.all([
        prop.default_assigned_crew_id
          ? supabase.from('crews').select('name').eq('id', prop.default_assigned_crew_id).maybeSingle()
          : Promise.resolve({ data: null as { name: string } | null }),
        prop.default_assigned_employee_id
          ? supabase
              .from('employees')
              .select('first_name, last_name')
              .eq('id', prop.default_assigned_employee_id)
              .maybeSingle()
          : Promise.resolve({ data: null as { first_name: string; last_name: string } | null }),
      ])
      if (cancelled) return
      setPropertyAssignmentLabels({
        crewName: crewRes.data?.name || null,
        employeeName: empRes.data
          ? `${empRes.data.first_name} ${empRes.data.last_name}`.trim()
          : null,
      })
    })()
    return () => {
      cancelled = true
    }
  }, [contract?.property_id])

  useEffect(() => {
    if (contract && id) {
      fetchActivities()
    }
  }, [contract, id])

  const fetchRenewalHistory = async (contractId: string) => {
    try {
      // Fetch parent contract if this is a renewal
      const { data: contractData } = await supabase
        .from('contracts')
        .select('parent_contract_id, original_contract_id')
        .eq('id', contractId)
        .single()

      if (contractData?.parent_contract_id) {
        const { data: parent } = await supabase
          .from('contracts')
          .select('id, contract_number, title, start_date, end_date, status')
          .eq('id', contractData.parent_contract_id)
          .single()
        if (parent) setParentContract(parent as Contract)
      }

      // Fetch all contracts that renewed from this one
      const { data: renewals } = await supabase
        .from('contracts')
        .select('id, contract_number, title, start_date, end_date, status, renewal_date')
        .eq('parent_contract_id', contractId)
        .order('renewal_date', { ascending: false })

      if (renewals) {
        setRenewalContracts(renewals as Contract[])
      }
    } catch (error) {
      console.error('Error fetching renewal history:', error)
    }
  }

  // Fetch jobs and templates when Jobs tab is active
  useEffect(() => {
    if (id && activeTab === 'jobs') {
      fetchContractJobs(id)
      fetchJobTemplates(id)
    }
  }, [id, activeTab])

  useEffect(() => {
    if (!contract || !contract.is_recurring || visitGroups.length === 0) {
      setProjectedRecurringDates([])
      return
    }

    const today = new Date()
    const horizonEnd = new Date(today)
    horizonEnd.setDate(horizonEnd.getDate() + 365)
    const contractStart = contract.start_date ? new Date(contract.start_date) : today
    const contractEnd = contract.end_date ? new Date(contract.end_date) : horizonEnd
    const start = contractStart > today ? contractStart : today
    const end = contractEnd < horizonEnd ? contractEnd : horizonEnd

    const isMatch = (
      date: Date,
      recurrenceType: string,
      recurrenceDays: number[],
      anchorDate: Date
    ) => {
      const dow = date.getDay()
      if (recurrenceType === 'daily') return true
      if (recurrenceType === 'weekly') return recurrenceDays.includes(dow)
      if (recurrenceType === 'bi_weekly') {
        if (!recurrenceDays.includes(dow)) return false
        const diffDays = Math.floor(
          (date.getTime() - anchorDate.getTime()) / (1000 * 60 * 60 * 24)
        )
        const weekDiff = Math.floor(diffDays / 7)
        return weekDiff % 2 === 0
      }
      if (recurrenceType === 'monthly') return date.getDate() === anchorDate.getDate()
      if (recurrenceType === 'custom_interval') return recurrenceDays.includes(dow)
      return false
    }

    const projected = new Set<string>()
    for (const group of visitGroups.filter((g) => g.is_active !== false)) {
      const recurrenceType = group.recurrence_type || 'weekly'
      const recurrenceDays = group.recurrence_days || []
      const anchorDate = group.start_date ? new Date(group.start_date) : start
      let cursor = new Date(start)
      while (cursor <= end) {
        if (isMatch(cursor, recurrenceType, recurrenceDays, anchorDate)) {
          projected.add(cursor.toISOString().split('T')[0])
        }
        cursor.setDate(cursor.getDate() + 1)
      }
    }

    setProjectedRecurringDates(Array.from(projected).sort())
  }, [contract, visitGroups])

  useEffect(() => {
    // Refresh visit groups whenever the Services & Schedule tab is opened so
    // the Crew Visits section reflects the latest saved state.
    if (id && activeTab === 'services') {
      fetchVisitGroups(id)
    }
  }, [id, activeTab])

  const fetchActivities = async () => {
    if (!id) return
    try {
      const acts = await getEntityActivities('contract', id)

      // Also fetch proposal activities if contract was created from a proposal
      // Get proposal_id from contract state or fetch it from the contract
      const proposalId = contract?.proposal_id
      if (proposalId) {
        try {
          const proposalActs = await getEntityActivities('proposal', proposalId)
          // Merge proposal activities with contract activities, marking them as from proposal
          const proposalActsWithSource = proposalActs.map((act: ActivityLog) => ({
            ...act,
            description: `[From Proposal] ${act.description}`,
            metadata: {
              ...act.metadata,
              source: 'proposal',
              proposal_id: proposalId,
            },
          }))
          // Combine and sort by date (newest first)
          const allActs = [...acts, ...proposalActsWithSource].sort((a, b) => {
            const dateA = new Date(a.created_at || 0).getTime()
            const dateB = new Date(b.created_at || 0).getTime()
            return dateB - dateA
          })
          setActivities(allActs)
        } catch (proposalError) {
          console.error('Error fetching proposal activities:', proposalError)
          // If proposal activities fail, just use contract activities
          setActivities(acts)
        }
      } else {
        setActivities(acts)
      }
    } catch (error) {
      console.error('Error fetching activities:', error)
    }
  }

  const fetchContract = async (contractId: string) => {
    try {
      setLoading(true)

      const { data: contractData, error: contractError } = await supabase
        .from('contracts')
        .select(
          `
          *,
          customer:customers(id, company_name, contact_first_name, contact_last_name, email, phone, address, address_line_2, city, state, zip_code),
          property:properties(id, name, address, city, state, zip_code),
          proposal:proposals!contracts_proposal_id_fkey(id, title, proposal_number)
        `
        )
        .eq('id', contractId)
        .single()

      if (contractError) throw contractError

      console.log('ContractDetails - Fetched contract data:', contractData)
      console.log('ContractDetails - Is recurring:', contractData.is_recurring)
      console.log('ContractDetails - Recurrence days:', contractData.recurrence_days)
      console.log('ContractDetails - DSNY integration:', contractData.dsny_integration)

      setContract(contractData)
      try {
        setContractProperties(await fetchContractProperties(contractId))
      } catch {
        setContractProperties([])
      }

      try {
        const milestones = await fetchContractMilestones(contractId)
        setBillingMilestones(milestones)
      } catch {
        setBillingMilestones([])
      }

      // Fetch notes - handle both JSONB array and JSON string
      let parsedNotes: Array<{ timestamp: string; note: string }> = []
      if (contractData.notes) {
        if (Array.isArray(contractData.notes)) {
          parsedNotes = contractData.notes
        } else if (typeof contractData.notes === 'string') {
          try {
            const parsed = JSON.parse(contractData.notes)
            if (Array.isArray(parsed)) {
              parsedNotes = parsed
            }
          } catch (e) {
            console.warn('Failed to parse notes as JSON:', e)
            // If it's a plain string, convert to array format
            if (contractData.notes.trim()) {
              parsedNotes = [{
                timestamp: new Date().toISOString(),
                note: contractData.notes
              }]
            }
          }
        }
      }
      setNotes(parsedNotes)
      console.log('Fetched notes:', parsedNotes)
    } catch (error) {
      console.error('Error fetching contract:', error)
      alert('Error loading contract details')
    } finally {
      setLoading(false)
    }
  }

  const addNote = async () => {
    if (!newNote.trim() || !id) return

    setSavingNote(true)
    try {
      const noteToAdd: { timestamp: string; note: string } = {
        timestamp: new Date().toISOString(),
        note: newNote.trim(),
      }

      const updatedNotes = [noteToAdd, ...notes]

      console.log('Saving notes:', updatedNotes)

      const { error } = await supabase
        .from('contracts')
        .update({ notes: updatedNotes }) // Supabase should handle JSONB arrays automatically
        .eq('id', id)

      if (error) throw error

      setNotes(updatedNotes)
      setNewNote('')

      // Update local contract state
      setContract((prev) => (prev ? { ...prev, notes: updatedNotes } : null))

      // Log activity
      await logActivity({
        activity_type: 'note_added',
        entity_type: 'contract',
        entity_id: id!,
        description: 'Note added to contract',
      })
    } catch (error) {
      console.error('Error adding note:', error)
      alert('Error adding note')
    } finally {
      setSavingNote(false)
    }
  }

  const removeNote = async (index: number) => {
    if (!id) return

    setSavingNote(true)
    try {
      const updatedNotes = notes.filter((_, i) => i !== index)

      const { error } = await supabase
        .from('contracts')
        .update({ notes: updatedNotes.length > 0 ? updatedNotes : null })
        .eq('id', id)

      if (error) throw error

      setNotes(updatedNotes)

      // Update local contract state
      setContract((prev) => (prev ? { ...prev, notes: updatedNotes } : null))

      // Log activity
      await logActivity({
        activity_type: 'note_removed',
        entity_type: 'contract',
        entity_id: id!,
        description: 'Note removed from contract',
      })
    } catch (error) {
      console.error('Error removing note:', error)
      alert('Error removing note')
    } finally {
      setSavingNote(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey && e.key === 'Enter') {
      addNote()
    }
  }

  const fetchContractServices = async (contractId: string) => {
    try {
      const { data, error } = await supabase
        .from('contract_services')
        .select('*')
        .eq('contract_id', contractId)

      if (error) throw error
      contractServicesRef.current = data || []
      setContractServices(data || [])
    } catch (error) {
      console.error('Error fetching contract services:', error)
    }
  }

  const fetchVisitGroups = async (contractId: string) => {
    try {
      const { data: groups, error: groupsError } = await supabase
        .from('contract_visit_groups')
        .select('*')
        .eq('contract_id', contractId)
        .order('created_at', { ascending: true })

      if (groupsError) {
        // Table may not exist before migration is applied
        console.warn('Visit groups not available yet:', groupsError)
        setVisitGroups([])
        setVisitGroupsLoaded(true)
        return
      }

      const groupIds = (groups || []).map((g: any) => g.id)

      if (groupIds.length > 0) {
        const { data: mappings, error: mappingsError } = await supabase
          .from('contract_visit_group_services')
          .select(
            'visit_group_id, contract_service_id, schedule_mode, recurrence_type, recurrence_days, recurrence_weekday, recurrence_occurrence, recurrence_day_of_month, recurrence_nearest_visit'
          )
          .in('visit_group_id', groupIds)

        if (!mappingsError && mappings) {
          const normalized: ContractVisitGroup[] = (groups || []).map((g: any) =>
            normalizeVisitGroupFromDb(
              g,
              mappings.filter((m: any) => m.visit_group_id === g.id),
              contractId
            )
          )
          const scheduleRows = hydrateDsnyScheduleRowsFromContractServices(
            contractServicesRef.current,
            mappings.map((m: any) => ({
              contract_service_id: m.contract_service_id,
              schedule_mode: m.schedule_mode,
              recurrence_type: m.recurrence_type,
              recurrence_weekday: m.recurrence_weekday,
              recurrence_occurrence: m.recurrence_occurrence,
              recurrence_day_of_month: m.recurrence_day_of_month,
              recurrence_nearest_visit: m.recurrence_nearest_visit,
              visit_group_name: (groups || []).find((g: any) => g.id === m.visit_group_id)?.name,
              visit_group_dsny_integration: !!(groups || []).find(
                (g: any) => g.id === m.visit_group_id
              )?.dsny_integration,
            }))
          )
          const ensured = ensureDsnyVisitGroupsForDisplay(
            normalized as unknown as SharedContractVisitGroup[],
            contractServicesRef.current,
            { contractId, scheduleRows }
          )
          // Belt-and-suspenders: never lose DB assignees to ensure/heal.
          setVisitGroups(
            preserveVisitGroupTargetAssignees(
              normalized as unknown as SharedContractVisitGroup[],
              ensured
            ) as ContractVisitGroup[]
          )
          setVisitGroupsLoaded(true)
          return
        }
      }

      const normalized: ContractVisitGroup[] = (groups || []).map((g: any) =>
        normalizeVisitGroupFromDb(g, [], contractId)
      )

      const ensured = ensureDsnyVisitGroupsForDisplay(
        normalized as unknown as SharedContractVisitGroup[],
        contractServicesRef.current,
        { contractId }
      )
      setVisitGroups(
        preserveVisitGroupTargetAssignees(
          normalized as unknown as SharedContractVisitGroup[],
          ensured
        ) as ContractVisitGroup[]
      )
      setVisitGroupsLoaded(true)
    } catch (error) {
      console.error('Error fetching visit groups:', error)
      setVisitGroups([])
      setVisitGroupsLoaded(true)
    }
  }

  const deleteVisitGroup = async (groupId: string) => {
    const target = visitGroups.find((g) => g.id === groupId)
    if (!target) return
    if (!window.confirm(`Delete visit group "${target.name}"?`)) return

    try {
      if (!groupId.startsWith('temp-')) {
        const { error } = await supabase.from('contract_visit_groups').delete().eq('id', groupId)
        if (error) throw error
      }
      setVisitGroups((prev) => prev.filter((g) => g.id !== groupId))
    } catch (error) {
      console.error('Error deleting visit group:', error)
      alert('Failed to delete visit group: ' + (error as any).message)
    }
  }

  const saveVisitGroups = async () => {
    if (!id) return
    try {
      setSavingVisitGroups(true)
      let groupsToPersist = visitGroups as unknown as SharedContractVisitGroup[]
      if (isDsnyMaintenance) {
        groupsToPersist = ensureDsnyVisitGroupsForDisplay(
          syncVisitGroupAssigneesFromScheduleRows(groupsToPersist, dsnyScheduleRows as any),
          contractServicesRef.current as any,
          {
            scheduleRows: dsnyScheduleRows as any,
            contractId: id,
            contractStartDate: contract?.start_date || null,
            contractEndDate: contract?.end_date || null,
          }
        ) as unknown as SharedContractVisitGroup[]
      }
      // Same shared save as ContractEdit (atomic RPC when available)
      await replaceContractVisitGroups(id, groupsToPersist)
      await fetchVisitGroups(id)
      alert('Visit groups saved successfully.')
    } catch (error) {
      console.error('Error saving visit groups:', error)
      alert('Failed to save visit groups: ' + (error as any).message)
    } finally {
      setSavingVisitGroups(false)
    }
  }

  const fetchContractLineItems = async (contractId: string) => {
    try {
      const { data, error } = await supabase
        .from('contract_line_items')
        .select(
          `
          *,
          service_item:service_items(id, name),
          proposal_line_item:proposal_line_items(id, description, proposal_id)
        `
        )
        .eq('contract_id', contractId)
        .order('sort_order', { ascending: true })

      if (error) {
        // Table might not exist yet, that's okay
        console.warn('Error fetching contract line items (table may not exist):', error)
        return
      }

      console.log('Fetched contract line items with connections:', data)
      setLineItems(data || [])
    } catch (error) {
      console.error('Error fetching contract line items:', error)
    }
  }

  const fetchContractJobs = async (contractId: string) => {
    try {
      setLoadingJobs(true)
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('contract_id', contractId)
        .order('scheduled_date', { ascending: true })

      if (error) throw error
      setContractJobs(data || [])
    } catch (error) {
      console.error('Error fetching contract jobs:', error)
    } finally {
      setLoadingJobs(false)
    }
  }

  const fetchJobTemplates = async (contractId: string) => {
    try {
      const { data, error } = await supabase
        .from('contract_job_templates')
        .select('*')
        .eq('contract_id', contractId)
        .order('scheduled_date', { ascending: true })

      if (error) throw error
      setJobTemplates(data || [])
    } catch (error) {
      console.error('Error fetching job templates:', error)
    }
  }

  const fetchInvoices = async (contractId: string) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('id, invoice_number, total_amount, status, created_at')
        .eq('contract_id', contractId)
        .order('created_at', { ascending: false })

      if (error) {
        // Invoices table might not exist, that's okay
        console.warn('Error fetching invoices (table may not exist):', error)
        return
      }
      setInvoices(data || [])
    } catch (error) {
      console.error('Error fetching invoices:', error)
    }
  }

  const formatDate = (date: string) => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const getStatusColor = getContractStatusColor

  const formatSchedule = (contract: Contract) => {
    if (contract.contract_type === 'as_needed') {
      return 'Ongoing (as needed)'
    }
    if (!contract.is_recurring) {
      return 'One-time contract'
    }

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const recurringDays = contract.recurrence_days || []
    const daysText = recurringDays.map((day) => dayNames[day]).join(', ')

    let scheduleText = `${contract.recurrence_type || 'Custom'}`
    const interval = Number((contract as any).recurrence_interval || 0)
    if (interval > 1) {
      scheduleText += ` (every ${interval})`
    }
    if (daysText) {
      scheduleText += ` (${daysText})`
    }

    // Add DSNY info if available
    if (contract.dsny_integration && contract.dsny_collection_types?.length) {
      const dsnyTypes = contract.dsny_collection_types.join(', ')
      scheduleText += ` + DSNY (${dsnyTypes})`
    }

    return scheduleText
  }

  if (loading) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <p className="text-lg text-gray-600">Loading contract details...</p>
      </MobilePage>
    )
  }

  if (!contract) {
    return (
      <MobilePage className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Contract not found</h3>
        <p className="text-gray-500 mb-6">The requested contract could not be found.</p>
        <button
          type="button"
          onClick={() => navigate('/contracts')}
          className={mobile.primaryBtn}
        >
          Back to Contracts
        </button>
      </MobilePage>
    )
  }

  const handleBack = () => {
    if (returnTo) {
      navigate(decodeURIComponent(returnTo))
    } else {
      navigate('/contracts')
    }
  }
  const backLabel = returnTo ? 'Back' : 'Back to Contracts'

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title={contract.title}
        subtitle={`Contract #${contract.contract_number}`}
        onBack={handleBack}
        backLabel={backLabel}
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate(`/contracts/edit/${contract.id}`)}
              className={`${mobile.primaryBtn} !min-h-[40px] !px-3 text-xs sm:text-sm hidden sm:inline-flex`}
            >
              <EditIcon className="w-4 h-4" />
              Edit
            </button>
            <button
              type="button"
              onClick={() => setActionsOpen(true)}
              className={`${mobile.secondaryBtn} !min-h-[40px] !px-3 text-xs sm:text-sm`}
              aria-label="More actions"
            >
              <MoreHorizontalIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Actions</span>
            </button>
          </>
        }
      />

      <div className={`${mobile.surface} mb-3 flex flex-col min-h-0`}>
        <ViewTabBar
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'services', label: 'Services', full: 'Services & Schedule' },
            { id: 'billing', label: 'Billing', full: 'Billing & Financials' },
            { id: 'notes', label: 'Notes' },
            { id: 'jobs', label: 'Jobs' },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
        />

        {/* Tab Content */}
        <div className="p-3 sm:p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <ViewStatCard
                  icon={<BriefcaseIcon className="w-5 h-5 text-purple-600" />}
                  title="Jobs"
                  value={contractJobs.length}
                />
                <ViewStatCard
                  icon={<ClipboardListIcon className="w-5 h-5 text-cyan-600" />}
                  title="Invoices"
                  value={invoices.length}
                />
                <ViewStatCard
                  icon={<DollarSignIcon className="w-5 h-5 text-blue-600" />}
                  title="Amount"
                  value={
                    contract.total_amount
                      ? `$${Number(contract.total_amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                      : '—'
                  }
                />
              </div>

              {id && (
                <ConversationsPanel
                  entityType="contract"
                  entityId={id}
                  customerId={contract.customer?.id}
                />
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <ViewSection
                    title="Contract Information"
                    icon={<FileTextIcon className="w-6 h-6" />}
                    tone="blue"
                  >

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Status</label>
                        <div className="mt-1">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                              contract.status
                            )}`}
                          >
                            {formatContractStatusLabel(contract.status)}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-500">Contract Type</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {formatContractTypeLabel(contract.contract_type)}
                        </p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-500">Template Type</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {contract.template_type ? contract.template_type.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'N/A'}
                        </p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-500">Service Type</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {getServiceTypeLabel(contract.service_type) || contract.service_type || 'N/A'}
                        </p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Billing Frequency
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {contract.billing_frequency ? contract.billing_frequency.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'N/A'}
                        </p>
                      </div>

                      {showBillingDayPicker(contract.billing_frequency) && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">
                            Bill on day of month
                          </label>
                          <p className="mt-1 text-sm text-gray-900">
                            {formatBillingDayOfMonth(contract.billing_day_of_month)}
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Billing Method
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {contract.billing_method ? contract.billing_method.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {contract.description && (
                      <div className="mt-4">
                        <label className="text-sm font-medium text-gray-500">Description</label>
                        <p className="mt-1 text-sm text-gray-900">{contract.description}</p>
                      </div>
                    )}
                  </ViewSection>

                  <ViewSection
                    title="Customer & Property"
                    icon={<BuildingIcon className="w-6 h-6" />}
                    tone="teal"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Customer</label>
                        {contract.customer?.id ? (
                          <button
                            onClick={() => {
                              if (contract.customer?.id) {
                                const returnTo = encodeURIComponent(`/contracts/details/${id}`)
                                navigate(`/customers/view/${contract.customer.id}?returnTo=${returnTo}`)
                              }
                            }}
                            className="mt-1 text-sm text-blue-600 hover:text-blue-800 hover:underline text-left block"
                          >
                            {contract.customer?.company_name ||
                              `${contract.customer?.contact_first_name} ${contract.customer?.contact_last_name}`}
                          </button>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900">
                            {contract.customer?.company_name ||
                              `${contract.customer?.contact_first_name} ${contract.customer?.contact_last_name}` || 'N/A'}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          {contractProperties.filter((link) => link.status === 'active').length > 1
                            ? 'Covered buildings'
                            : 'Property'}
                        </label>
                        {contractProperties.filter((link) => link.status === 'active').length > 0 ? (
                          <div className="mt-1 space-y-1">
                            {contractProperties
                              .filter((link) => link.status === 'active')
                              .map((link) => (
                                <button
                                  key={link.id}
                                  onClick={() => navigate(`/properties/view/${link.property_id}`)}
                                  className="block text-sm text-blue-600 hover:text-blue-800 hover:underline text-left"
                                >
                                  {link.property?.name || link.property?.address || 'Unnamed property'}
                                  {link.is_primary ? ' (primary)' : ''}
                                </button>
                              ))}
                          </div>
                        ) : contract.property?.id ? (
                          <button
                            onClick={() => {
                              if (contract.property?.id) {
                                const returnTo = encodeURIComponent(`/contracts/details/${id}`)
                                navigate(`/properties/view/${contract.property.id}?returnTo=${returnTo}`)
                              }
                            }}
                            className="mt-1 text-sm text-blue-600 hover:text-blue-800 hover:underline text-left block"
                          >
                            {contract.property?.name || contract.property?.address || 'N/A'}
                          </button>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900">
                            {contract.property?.name || contract.property?.address || 'N/A'}
                          </p>
                        )}
                      </div>
                    </div>
                  </ViewSection>
                </div>

                <div className="space-y-6">
                  <ViewSection title="Financial" icon={<DollarSignIcon className="w-6 h-6" />} tone="emerald">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Amount Per Invoice</label>
                        <p className="text-sm text-gray-900">
                          {contract.total_amount
                            ? `$${contract.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : 'N/A'}
                        </p>
                      </div>
                      {contract.contract_total_amount !== undefined && contract.contract_total_amount !== null && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Total Contract Amount</label>
                          <p className="text-sm text-gray-900">
                            ${contract.contract_total_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Billing Frequency
                        </label>
                        <p className="text-sm text-gray-900">
                          {contract.billing_frequency ? contract.billing_frequency.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'N/A'}
                        </p>
                      </div>
                      {showBillingDayPicker(contract.billing_frequency) && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">
                            Bill on day of month
                          </label>
                          <p className="text-sm text-gray-900">
                            {formatBillingDayOfMonth(contract.billing_day_of_month)}
                          </p>
                        </div>
                      )}
                      {contract.sales_tax_status && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Sales Tax Status</label>
                          <p className="text-sm text-gray-900">
                            {contract.sales_tax_status.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                          </p>
                        </div>
                      )}
                    </div>
                  </ViewSection>

                  {(contract.proposal || contractJobs.length > 0 || invoices.length > 0) && (
                    <ViewSection title="Related" icon={<ClipboardListIcon className="w-6 h-6" />} tone="orange">
                      <div className="space-y-3">
                        {contract.proposal && (contract.proposal_id || contract.proposal.id) && (
                          <div className="flex items-center gap-3">
                            <FileTextIcon className="w-5 h-5 text-gray-400" />
                            <div className="flex-1">
                              <label className="text-sm font-medium text-gray-500">
                                Created from Proposal
                              </label>
                              <button
                                onClick={() => {
                                  const proposalId = contract.proposal_id || contract.proposal?.id
                                  if (proposalId) {
                                    const returnTo = encodeURIComponent(`/contracts/details/${id}`)
                                    navigate(`/proposals/view/${proposalId}?returnTo=${returnTo}`)
                                  }
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left block mt-1"
                              >
                                {contract.proposal.proposal_number
                                  ? `Proposal ${contract.proposal.proposal_number} \u2192`
                                  : 'View Proposal \u2192'}
                              </button>
                              {contract.proposal.title && (
                                <p className="text-xs text-gray-500 mt-0.5">{contract.proposal.title}</p>
                              )}
                            </div>
                          </div>
                        )}
                        {contractJobs.length > 0 && (
                          <div className="flex items-center gap-3">
                            <BriefcaseIcon className="w-5 h-5 text-gray-400" />
                            <div className="flex-1">
                              <label className="text-sm font-medium text-gray-500">Jobs</label>
                              <button
                                onClick={() => {
                                  const returnTo = encodeURIComponent(`/contracts/details/${id}`)
                                  navigate(`/contracts/details/${id}?returnTo=${returnTo}&tab=jobs`)
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left block mt-1"
                              >
                                {contractJobs.length} {contractJobs.length === 1 ? 'job' : 'jobs'}
                              </button>
                            </div>
                          </div>
                        )}
                        {invoices.length > 0 && (
                          <div className="flex items-center gap-3">
                            <ClipboardListIcon className="w-5 h-5 text-gray-400" />
                            <div className="flex-1">
                              <label className="text-sm font-medium text-gray-500">Invoices</label>
                              <button
                                onClick={() => {
                                  const returnTo = encodeURIComponent(`/contracts/details/${id}`)
                                  navigate(`/billing?contract=${id}&returnTo=${returnTo}`)
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left block mt-1"
                              >
                                {invoices.length} {invoices.length === 1 ? 'invoice' : 'invoices'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </ViewSection>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div className="space-y-6">
              <ContractAssignmentSummary
                assignmentSource={contract.assignment_source}
                defaultCrewId={contract.default_assigned_crew_id}
                defaultEmployeeId={contract.default_assigned_employee_id}
                propertyDefaultCrewName={propertyAssignmentLabels?.crewName}
                propertyDefaultEmployeeName={propertyAssignmentLabels?.employeeName}
                visitGroups={visitGroups}
                crews={crews}
                employees={employees}
                showDsnyVisitTargets={isDsnyMaintenance}
              />

              <ViewSection title="Contract Type" icon={<FileTextIcon className="w-6 h-6" />} tone="blue">
                <div className="flex items-center gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Type</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatContractTypeLabel(contract.contract_type)}
                    </p>
                  </div>
                </div>
              </ViewSection>

              <ViewSection title="Services" icon={<ClipboardListIcon className="w-6 h-6" />} tone="sky">
                <div className="space-y-4">
                  {contractServices.length === 0 ? (
                    <ViewEmptyState
                      tone="sky"
                      icon={<ClipboardListIcon className="w-10 h-10 text-sky-300" />}
                      message={
                        contract.service_type
                          ? `No individual services found. Contract service type: ${getServiceTypeLabel(contract.service_type) || contract.service_type}`
                          : 'No individual services found. This contract may use legacy schedule structure.'
                      }
                    />
                  ) : (
                    contractServices.map((service) => {
                      const isExpanded = expandedServices.has(service.id)
                      const scheduleSummary = describeContractServiceSchedule(service, visitGroups)
                      const hasDsnyScheduleView =
                        service.dsny_integration ||
                        service.schedule?.dsny_integration ||
                        (service.garbage_schedule && service.garbage_schedule.length > 0) ||
                        (service.schedule?.garbage_schedule &&
                          service.schedule.garbage_schedule.length > 0) ||
                        (service.recycling_schedule && service.recycling_schedule.length > 0) ||
                        (service.schedule?.recycling_schedule &&
                          service.schedule.recycling_schedule.length > 0) ||
                        (service.organics_schedule && service.organics_schedule.length > 0) ||
                        (service.schedule?.organics_schedule &&
                          service.schedule.organics_schedule.length > 0) ||
                        (service.bulk_schedule && service.bulk_schedule.length > 0) ||
                        (service.schedule?.bulk_schedule &&
                          service.schedule.bulk_schedule.length > 0) ||
                        (service.interior_cleaning_schedule &&
                          service.interior_cleaning_schedule.length > 0) ||
                        (service.schedule?.interior_cleaning_schedule &&
                          service.schedule.interior_cleaning_schedule.length > 0) ||
                        (service.recurrence_days && service.recurrence_days.length > 0) ||
                        (service.schedule?.recurrence_days &&
                          service.schedule.recurrence_days.length > 0)
                      return (
                        <div
                          key={service.id}
                          className="border border-gray-200 rounded-lg overflow-hidden"
                        >
                          {/* Collapsed Header */}
                          <button
                            type="button"
                            onClick={() => {
                              const newExpanded = new Set(expandedServices)
                              if (isExpanded) {
                                newExpanded.delete(service.id)
                              } else {
                                newExpanded.add(service.id)
                              }
                              setExpandedServices(newExpanded)
                            }}
                            className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-wrap">
                              <h3 className="font-medium text-gray-900">{service.service_name}</h3>
                              <p className="text-sm text-gray-600">
                                ${service.amount?.toFixed(2) || '0.00'}
                              </p>
                              {(service.recurrence_type || service.schedule?.recurrence_type) && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                  {String(service.recurrence_type || service.schedule?.recurrence_type).replace(/_/g, ' ')}
                                </span>
                              )}
                              {contract?.contract_type === 'one_time' && (service.scheduled_date || service.scheduled_time) ? (
                                <span className="text-xs text-blue-600">
                                  {service.scheduled_date ? new Date(service.scheduled_date).toLocaleDateString() : ''}
                                  {service.scheduled_date && service.scheduled_time ? ' at ' : ''}
                                  {service.scheduled_time || ''}
                                </span>
                              ) : (
                                (service.dsny_integration || service.schedule?.dsny_integration) && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    DSNY
                                  </span>
                                )
                              )}
                            </div>
                            <ChevronDownIcon
                              className={`w-5 h-5 text-gray-400 transition-transform ${
                                isExpanded ? 'transform rotate-180' : ''
                              }`}
                            />
                          </button>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="p-4 pt-0 border-t border-gray-200">
                              {/* Service Schedule */}
                              {hasDsnyScheduleView && (
                                <div className="mt-3">
                                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                                    Schedule
                                  </h4>
                                  <ScheduleView
                                    garbageSchedule={service.garbage_schedule || service.schedule?.garbage_schedule || []}
                                    recyclingSchedule={service.recycling_schedule || service.schedule?.recycling_schedule || []}
                                    organicsSchedule={service.organics_schedule || service.schedule?.organics_schedule || []}
                                    bulkSchedule={service.bulk_schedule || service.schedule?.bulk_schedule || []}
                                    interiorCleaningSchedule={(
                                      (service.interior_cleaning_schedule || service.schedule?.interior_cleaning_schedule || [])
                                    ).map((day: string | number) => typeof day === 'string' ? parseInt(day) : day)}
                                    masterWeeklySchedule={service.recurrence_days || service.schedule?.recurrence_days || []}
                                    manualSchedules={service.schedule?.manual_schedules || []}
                                  />
                                </div>
                              )}
                              {!hasDsnyScheduleView && scheduleSummary && (
                                <div className="mt-3">
                                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                                    Schedule
                                  </h4>
                                  <p className="text-sm text-gray-700">{scheduleSummary}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })
                  )}
                </div>
              </ViewSection>

              {contractServices.length === 0 &&
                (contract.dsny_integration ||
                  (contract.garbage_schedule && contract.garbage_schedule.length > 0) ||
                  (contract.recycling_schedule && contract.recycling_schedule.length > 0) ||
                  (contract.organics_schedule && contract.organics_schedule.length > 0) ||
                  (contract.bulk_schedule && contract.bulk_schedule.length > 0) ||
                  (contract.interior_cleaning_schedule &&
                    contract.interior_cleaning_schedule.length > 0) ||
                  (contract.manual_schedules && contract.manual_schedules.length > 0) ||
                  (contract.master_weekly_schedule &&
                    contract.master_weekly_schedule.length > 0)) && (
                  <ViewSection title="Contract Schedule" icon={<CalendarIcon className="w-6 h-6" />} tone="sky">
                    <ScheduleView
                      garbageSchedule={contract.garbage_schedule || []}
                      recyclingSchedule={contract.recycling_schedule || []}
                      organicsSchedule={contract.organics_schedule || []}
                      bulkSchedule={contract.bulk_schedule || []}
                      interiorCleaningSchedule={(contract.interior_cleaning_schedule || []).map(
                        (day) => parseInt(day)
                      )}
                      masterWeeklySchedule={contract.master_weekly_schedule || []}
                      manualSchedules={contract.manual_schedules || []}
                    />
                  </ViewSection>
                )}

              {contract.is_recurring && (
                <ViewSection title="Schedule" icon={<CalendarIcon className="w-6 h-6" />} tone="sky">

                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <CalendarIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <label className="text-sm font-medium text-gray-500">Recurrence</label>
                        <p className="text-sm text-gray-900">{formatSchedule(contract)}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <ClockIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <label className="text-sm font-medium text-gray-500">Contract Period</label>
                        <p className="text-sm text-gray-900">
                          {formatDate(contract.start_date || '')} -{' '}
                          {formatDate(contract.end_date || '')}
                        </p>
                      </div>
                    </div>

                    {contract.dsny_integration && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <h3 className="text-sm font-medium text-blue-900 mb-2">DSNY Integration</h3>
                        <div className="text-sm text-blue-800">
                          <p>
                            <strong>Collection Types:</strong>{' '}
                            {contract.dsny_collection_types?.join(', ') || 'None selected'}
                          </p>
                          {contract.interior_cleaning_schedule &&
                            contract.interior_cleaning_schedule.length > 0 && (
                              <p>
                                <strong>Interior Cleaning:</strong>{' '}
                                {contract.interior_cleaning_schedule.join(', ')}
                              </p>
                            )}
                        </div>
                      </div>
                    )}

                    {/* Schedule Preview */}
                    {contract.recurrence_days && contract.recurrence_days.length > 0 && (
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">Weekly Schedule</h3>
                        <div className="flex flex-wrap gap-2">
                          {contract.recurrence_days.map((day) => {
                            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
                            return (
                              <span
                                key={day}
                                className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800"
                              >
                                {dayNames[day]}
                              </span>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </ViewSection>
              )}

              {contract.contract_type === 'recurring' && (
                <ViewSection
                  title="Crew Visits"
                  icon={<BriefcaseIcon className="w-6 h-6" />}
                  tone="emerald"
                  actions={
                    <button
                      type="button"
                      onClick={() => setShowVisitBoard(!showVisitBoard)}
                      className="px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors flex-shrink-0"
                    >
                      {showVisitBoard ? 'Hide customization' : 'Customize visits'}
                    </button>
                  }
                >
                  <p className="text-sm text-gray-600 -mt-2 mb-4">
                    Each line is one recurring crew visit that generates jobs on the calendar.
                  </p>
                  {visitGroups.length === 0 ? (
                    <ViewEmptyState
                      tone="emerald"
                      message='No crew visits configured yet. Use "Customize visits" to set them up.'
                    />
                  ) : (
                    !showVisitBoard && (
                      <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                        {visitGroups.map((group) => {
                          const assignmentCount = getGroupAssignments(
                            group as unknown as SharedContractVisitGroup
                          ).length
                          const assigneeLabel = formatVisitGroupAssigneeLabel(
                            group,
                            crews,
                            employees
                          )
                          return (
                            <li
                              key={group.id}
                              className="px-4 py-2.5 flex items-center justify-between gap-3"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <span className="text-sm font-medium text-gray-900 truncate">
                                  {group.name || 'Visit'}
                                </span>
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                  {describeVisitGroupCadence(
                                    group as unknown as SharedContractVisitGroup
                                  )}
                                </span>
                                {assigneeLabel && (
                                  <span className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5 flex-shrink-0 truncate max-w-[14rem]">
                                    {assigneeLabel}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-gray-500 flex-shrink-0">
                                {assignmentCount} service{assignmentCount === 1 ? '' : 's'}
                              </span>
                            </li>
                          )
                        })}
                      </ul>
                    )
                  )}

                  {showVisitBoard && (
                    <div className="space-y-4 mt-2">
                      <VisitScheduleBoard
                        visitGroups={visitGroups as unknown as SharedContractVisitGroup[]}
                        onChange={(groups) =>
                          setVisitGroups(groups as unknown as ContractVisitGroup[])
                        }
                        contractServices={visitBoardServices as any}
                        contractStartDate={contract?.start_date}
                        contractEndDate={contract?.end_date}
                        contractId={id || ''}
                        showSaveButton
                        onSave={saveVisitGroups}
                        saving={savingVisitGroups}
                        onAdvancedSettings={(groupId) => setAdvancedVisitGroupId(groupId)}
                        templates={vgTemplates}
                      />
                      {advancedVisitGroupId && (
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-gray-900">
                              Checklist &amp; advanced
                            </h3>
                            <button
                              type="button"
                              onClick={() => setAdvancedVisitGroupId(null)}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              Close
                            </button>
                          </div>
                          <VisitGroupEditor
                            visitGroups={visitGroups.filter((g) => g.id === advancedVisitGroupId) as unknown as SharedContractVisitGroup[]}
                            onChange={(updated) => {
                              const updatedGroup = updated[0]
                              if (!updatedGroup) return
                              setVisitGroups((prev) =>
                                prev.map((g) =>
                                  g.id === updatedGroup.id ? (updatedGroup as ContractVisitGroup) : g
                                )
                              )
                            }}
                            contractServices={visitBoardServices as any}
                            contractStartDate={contract?.start_date}
                            contractEndDate={contract?.end_date}
                            contractId={id || ''}
                            showAddButton={false}
                            onDelete={(groupId) => deleteVisitGroup(groupId)}
                            templates={vgTemplates}
                            onSaveAsTemplate={saveGroupAsTemplate}
                            title=""
                            subtitle=""
                          />
                        </div>
                      )}
                    </div>
                  )}
                </ViewSection>
              )}
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="space-y-6">
              {(contract.start_date || contract.end_date) && (
                <ViewSection title="Contract Duration" icon={<CalendarIcon className="w-6 h-6" />} tone="blue">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Start Date</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatDate(contract.start_date || '')}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">End Date</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatDate(contract.end_date || '')}
                      </p>
                    </div>
                    {contract.start_date && contract.end_date && (
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium text-gray-500">Duration</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {Math.ceil((new Date(contract.end_date).getTime() - new Date(contract.start_date).getTime()) / (1000 * 60 * 60 * 24))} days
                        </p>
                      </div>
                    )}
                  </div>
                </ViewSection>
              )}

              {(parentContract || renewalContracts.length > 0) && (
                <ViewSection title="Renewal History" icon={<HistoryIcon className="w-6 h-6" />} tone="sky">

                  {parentContract && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Renewed From</label>
                          <p className="mt-1 text-sm text-gray-900">
                            <button
                              onClick={() => navigate(`/contracts/details/${parentContract.id}`)}
                              className="text-blue-600 hover:text-blue-800 underline"
                            >
                              {parentContract.contract_number} - {parentContract.title}
                            </button>
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(parentContract.start_date || '')} - {formatDate(parentContract.end_date || '')}
                          </p>
                        </div>
                        <RefreshCwIcon className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                  )}

                  {(contract as any).original_contract_id &&
                    (contract as any).original_contract_id !== (parentContract?.id || null) && (
                      <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <label className="text-sm font-medium text-gray-700">
                          Original Contract in Renewal Chain
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          <button
                            onClick={() =>
                              navigate(`/contracts/details/${(contract as any).original_contract_id}`)
                            }
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            View original contract &rarr;
                          </button>
                        </p>
                      </div>
                    )}

                  {renewalContracts.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Renewals ({renewalContracts.length})
                      </label>
                      <div className="space-y-2">
                        {renewalContracts.map((renewal) => (
                          <div
                            key={renewal.id}
                            className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <button
                                  onClick={() => navigate(`/contracts/details/${renewal.id}`)}
                                  className="text-sm font-medium text-blue-600 hover:text-blue-800 underline"
                                >
                                  {renewal.contract_number} - {renewal.title}
                                </button>
                                <p className="text-xs text-gray-500 mt-1">
                                  {formatDate(renewal.start_date || '')} - {formatDate(renewal.end_date || '')}
                                  {renewal.renewal_date && ` • Renewed: ${formatDate(renewal.renewal_date)}`}
                                </p>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                renewal.status === 'active' ? 'bg-green-100 text-green-800' :
                                renewal.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                                renewal.status === 'expired' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {renewal.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </ViewSection>
              )}

              <ViewSection title="Billing Configuration" icon={<DollarSignIcon className="w-6 h-6" />} tone="emerald">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {contract.billing_method === 'actual_usage' ? (
                    <>
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium text-gray-500">Billing Method</label>
                        <p className="mt-1 text-sm text-gray-900 font-medium text-blue-600">
                          Billing by Usage
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Amounts are calculated based on actual service usage and line items
                        </p>
                      </div>
                      {lineItems.length > 0 && (
                        <div className="md:col-span-2">
                          <label className="text-sm font-medium text-gray-500">Estimated from Line Items</label>
                          <p className="mt-1 text-sm text-gray-900">
                            ${lineItems.reduce((sum, item) => {
                              // For variable items, use unit_price; for fixed items, use total_price
                              if (item.is_variable) {
                                return sum + (item.unit_price || 0)
                              }
                              return sum + (item.total_price || ((item.quantity || 1) * (item.unit_price || 0)))
                            }, 0).toFixed(2)}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            This is an estimate based on current line items. Actual billing will vary based on usage.
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Amount Per Invoice</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {contract.total_amount ? `$${contract.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                        </p>
                      </div>

                      {contract.contract_total_amount !== undefined && contract.contract_total_amount !== null && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Total Contract Amount</label>
                          <p className="mt-1 text-sm text-gray-900">
                            ${contract.contract_total_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  <div>
                    <label className="text-sm font-medium text-gray-500">Billing Frequency</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {contract.billing_frequency ? contract.billing_frequency.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'N/A'}
                    </p>
                  </div>

                  {showBillingDayPicker(contract.billing_frequency) && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Bill on day of month</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatBillingDayOfMonth(contract.billing_day_of_month)}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-gray-500">Billing Method</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {contract.billing_method === 'actual_usage'
                        ? 'Billing by Usage'
                        : contract.billing_method
                          ? contract.billing_method.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                          : 'N/A'}
                    </p>
                  </div>
                </div>

                {contract.billing_frequency === 'progress' && billingMilestones.length > 0 && (
                  <div className="mt-6 border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Progress billing schedule</h3>
                    <ul className="space-y-2 lg:hidden">
                      {billingMilestones.map((m) => (
                        <li key={m.id} className="rounded-xl border border-gray-200 p-3 space-y-1">
                          <p className="text-sm font-medium text-gray-900">{m.label}</p>
                          <p className="text-xs text-gray-600">
                            {m.percentage}% · ${Number(m.amount).toFixed(2)} ·{' '}
                            <span className="capitalize">{m.status}</span>
                          </p>
                          {m.invoice_id ? (
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/invoice-generation?invoice=${m.invoice_id}`)
                              }
                              className="text-sm text-blue-600 hover:text-blue-800 underline"
                            >
                              View invoice
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-500 border-b">
                            <th className="py-2 pr-4">Milestone</th>
                            <th className="py-2 pr-4">%</th>
                            <th className="py-2 pr-4">Amount</th>
                            <th className="py-2 pr-4">Status</th>
                            <th className="py-2">Invoice</th>
                          </tr>
                        </thead>
                        <tbody>
                          {billingMilestones.map((m) => (
                            <tr key={m.id} className="border-b border-gray-100">
                              <td className="py-2 pr-4">{m.label}</td>
                              <td className="py-2 pr-4">{m.percentage}%</td>
                              <td className="py-2 pr-4">${Number(m.amount).toFixed(2)}</td>
                              <td className="py-2 pr-4 capitalize">{m.status}</td>
                              <td className="py-2">
                                {m.invoice_id ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      navigate(`/invoice-generation?invoice=${m.invoice_id}`)
                                    }
                                    className="text-blue-600 hover:text-blue-800 underline"
                                  >
                                    View invoice
                                  </button>
                                ) : (
                                  '—'
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </ViewSection>

              <ViewSection title="Payment Terms" icon={<FileTextIcon className="w-6 h-6" />} tone="indigo">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Payment Terms</label>
                    <p className="mt-1 text-sm text-gray-900">{contract.payment_terms || 'N/A'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Late Fee Percentage</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {contract.late_fee_percentage ? `${contract.late_fee_percentage}%` : 'N/A'}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Payment Method</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {(contract as any).payment_method
                        ? (contract as any).payment_method
                            .split('_')
                            .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                            .join(' ')
                        : 'N/A'}
                    </p>
                  </div>

                  {contract.sales_tax_status && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Sales Tax Status</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {contract.sales_tax_status.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </p>
                    </div>
                  )}

                  {contract.signed_date && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Signed Date</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatDate(contract.signed_date)}
                      </p>
                    </div>
                  )}

                  {(contract as any).cancel_date && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Cancel Date</label>
                      <p className="mt-1 text-sm text-red-700">
                        {formatDate((contract as any).cancel_date)}
                      </p>
                    </div>
                  )}
                </div>

                {contract.cancellation_terms && (
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-500">Cancellation Terms</label>
                    <p className="mt-1 text-sm text-gray-900">{contract.cancellation_terms}</p>
                  </div>
                )}
              </ViewSection>

              <ViewSection title="Contract Period" icon={<ClockIcon className="w-6 h-6" />} tone="sky">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <ClockIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <label className="text-sm font-medium text-gray-500">Start Date</label>
                      <p className="text-sm text-gray-900">
                        {formatDate(contract.start_date || '')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <ClockIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <label className="text-sm font-medium text-gray-500">End Date</label>
                      <p className="text-sm text-gray-900">{formatDate(contract.end_date || '')}</p>
                    </div>
                  </div>
                </div>
              </ViewSection>

              {/* Line Items */}
              {lineItems.length > 0 && (
                <ViewSection title="Line Items" icon={<ClipboardListIcon className="w-6 h-6" />} tone="orange">
                  {(() => {
                    const formatUnitType = (unitType: unknown) => {
                      const raw = String(unitType || '').toLowerCase().trim()
                      if (!raw) return 'N/A'
                      const map: Record<string, string> = {
                        monthly: 'Per Month',
                        hourly: 'Per Hour',
                        sqft: 'Per Sq Ft',
                        visit: 'Per Visit',
                        unit: 'Per Unit',
                        flat: 'Flat Rate',
                      }
                      if (map[raw]) return map[raw]
                      return raw
                        .split('_')
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(' ')
                    }
                    const lineTotal = (item: any) =>
                      item.is_variable
                        ? (item.unit_price || 0)
                        : item.total_price || (item.quantity || 1) * (item.unit_price || 0)
                    const grandTotal = lineItems
                      .reduce((sum, item) => sum + lineTotal(item), 0)
                      .toFixed(2)
                    const propertyIds = contractProperties
                      .filter((link) => link.status === 'active')
                      .map((link) => link.property_id)
                      .filter(Boolean)

                    const itemMeta = (item: any) => (
                      <>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {item.service_item && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              📦 {item.service_item.name}
                            </span>
                          )}
                          {item.contract_service && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              ⚙️ {item.contract_service.service_name}
                            </span>
                          )}
                          {item.proposal_line_item && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              🔗 From Proposal
                            </span>
                          )}
                        </div>
                        {id && (
                          <ContractLineItemQrRequirements
                            contractId={id}
                            lineItemId={item.id}
                            propertyIds={propertyIds}
                          />
                        )}
                      </>
                    )

                    return (
                      <>
                        <ul className="space-y-2 lg:hidden">
                          {lineItems.map((item) => (
                            <li
                              key={item.id}
                              className="rounded-xl border border-gray-200 p-3 space-y-1.5"
                            >
                              <p className="text-sm font-medium text-gray-900 break-words">
                                {item.description}
                              </p>
                              {itemMeta(item)}
                              <p className="text-xs text-gray-600">
                                Qty {item.quantity} · {formatUnitType(item.unit_type)}
                              </p>
                              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                                <span>Rate ${item.unit_price?.toFixed(2) || '0.00'}</span>
                                {item.is_variable ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    ${(item.unit_price || 0).toFixed(2)} (Variable)
                                  </span>
                                ) : (
                                  <span className="font-semibold text-gray-900">
                                    ${lineTotal(item).toFixed(2)}
                                  </span>
                                )}
                              </div>
                            </li>
                          ))}
                          <li className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 flex items-center justify-between text-sm">
                            <span className="font-medium text-gray-700">Total:</span>
                            <span className="font-bold text-gray-900">${grandTotal}</span>
                          </li>
                        </ul>
                        <div className="hidden lg:block overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Description
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Quantity
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Unit Type
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Unit Price
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Total
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {lineItems.map((item) => (
                                <tr key={item.id}>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    <div className="flex flex-col gap-1">
                                      <span>{item.description}</span>
                                      {itemMeta(item)}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{item.quantity}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">
                                    {formatUnitType(item.unit_type)}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                    ${item.unit_price?.toFixed(2) || '0.00'}
                                  </td>
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                    {item.is_variable ? (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                        ${(item.unit_price || 0).toFixed(2)} (Variable)
                                      </span>
                                    ) : (
                                      <span className="text-gray-900">
                                        ${lineTotal(item).toFixed(2)}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-gray-50">
                              <tr>
                                <td
                                  colSpan={4}
                                  className="px-4 py-3 text-sm font-medium text-gray-900 text-right"
                                >
                                  Total:
                                </td>
                                <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                  ${grandTotal}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </>
                    )
                  })()}
                </ViewSection>
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-6">
              <ViewSection title="Notes" icon={<StickyNoteIcon className="w-6 h-6" />} tone="blue">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Add Note</label>
                  <div className="flex gap-2">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      onKeyDown={handleKeyDown}
                      rows={3}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter a note (Ctrl+Enter to submit)"
                    />
                    <button
                      type="button"
                      onClick={addNote}
                      disabled={!newNote.trim() || savingNote}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingNote ? 'Saving...' : 'Add'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Press Ctrl+Enter to quickly submit</p>
                </div>

                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Notes History</h3>
                  {notes.length === 0 ? (
                    <p className="text-sm text-gray-500">No notes added yet</p>
                  ) : (
                    <div className="space-y-3">
                      {notes.map((note, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <p className="text-xs text-gray-500">
                              {new Date(note.timestamp).toLocaleString()}
                            </p>
                            <button
                              type="button"
                              onClick={() => removeNote(index)}
                              className="text-red-600 hover:text-red-800 text-xs"
                            >
                              Remove
                            </button>
                          </div>
                          <p className="text-sm text-gray-900">{note.note}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </ViewSection>

              <ViewSection title="Activity" icon={<HistoryIcon className="w-6 h-6" />} tone="indigo">
                {activities.length > 0 ? (
                  <div className="space-y-3">
                    {activities.map((activity) => (
                      <div key={activity.id} className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-700">{activity.description}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {formatActivityMeta(activity)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ViewEmptyState tone="indigo" message="No recent activity" />
                )}
              </ViewSection>
            </div>
          )}

          {activeTab === 'jobs' && (
            <div className="space-y-6">
              <ViewSection
                title="Contract Jobs"
                icon={<BriefcaseIcon className="w-6 h-6" />}
                tone="sky"
                actions={
                <div className="flex gap-2">
                  {contract?.is_recurring && contract?.status === 'active' && (
                    <button
                      type="button"
                      onClick={openGenerateJobsModal}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Generate Jobs (Range)
                    </button>
                  )}
                  <button
                    onClick={() => setJobsViewMode('calendar')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      jobsViewMode === 'calendar'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Calendar
                  </button>
                  <button
                    onClick={() => setJobsViewMode('list')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      jobsViewMode === 'list'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    List
                  </button>
                </div>
                }
              >
              {loadingJobs ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">Loading jobs...</p>
                </div>
              ) : (
                <>
                  {jobsViewMode === 'list' ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">All Jobs</h3>

                      {/* Created Jobs */}
                      {contractJobs.length > 0 && (
                        <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Created Jobs ({contractJobs.length})</h4>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Number</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scheduled Date</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {contractJobs.map((job) => (
                                <tr key={job.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-900">#{job.job_number}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900">{job.title}</td>
                                  <td className="px-4 py-3 text-sm text-gray-500">
                                    {job.scheduled_date ? formatDate(job.scheduled_date) : 'N/A'}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      job.status === 'completed' ? 'bg-green-100 text-green-800' :
                                      job.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                      job.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {job.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    <button
                                      onClick={() => navigate(`/jobs/details/${job.id}`)}
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      View
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        </div>
                      )}

                      {/* Scheduled Templates (Not yet created) */}
                      {jobTemplates.filter(t => !t.job_id).length > 0 && (
                        <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          Scheduled Jobs ({jobTemplates.filter(t => !t.job_id).length})
                        </h4>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scheduled Date</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {jobTemplates.filter(t => !t.job_id).map((template) => (
                                <tr key={template.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {formatDate(template.scheduled_date)}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {template.title || `Job - ${formatDate(template.scheduled_date)}`}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-500">
                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                                      {template.priority || 'medium'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => {
                                          setEditingTemplate(template)
                                          setNewTemplateDate(undefined)
                                          setTemplateEditorOpen(true)
                                        }}
                                        className="text-blue-600 hover:text-blue-800"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={async () => {
                                          if (window.confirm('Are you sure you want to delete this scheduled job?')) {
                                            try {
                                              await deleteJobTemplate(template.id)
                                              await fetchJobTemplates(id!)
                                            } catch (error) {
                                              console.error('Error deleting template:', error)
                                              alert('Error deleting template')
                                            }
                                          }
                                        }}
                                        className="text-red-600 hover:text-red-800"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        </div>
                      )}

                      {contractJobs.length === 0 && jobTemplates.filter(t => !t.job_id).length === 0 && (
                        <ViewEmptyState
                          tone="sky"
                          icon={<BriefcaseIcon className="w-10 h-10 text-sky-300" />}
                          message="No jobs found for this contract. Jobs will appear here once they are generated from the recurring schedule."
                        />
                      )}
                    </div>
                  ) : (
                    <ContractJobsCalendar
                      jobs={contractJobs}
                      templates={jobTemplates}
                      projectedDates={projectedRecurringDates}
                      onJobClick={(job) => {
                        navigate(`/jobs/details/${job.id}`)
                      }}
                      onTemplateClick={(template) => {
                        setEditingTemplate(template)
                        setTemplateEditorOpen(true)
                      }}
                      onDateClick={(date) => {
                        const dateStr = date.toISOString().split('T')[0]
                        setNewTemplateDate(dateStr)
                        setEditingTemplate(null)
                        setTemplateEditorOpen(true)
                      }}
                    />
                  )}
                </>
              )}
              </ViewSection>
            </div>
          )}
        </div>
      </div>

      <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:mt-4 lg:rounded-xl lg:border lg:border-gray-200">
        <button type="button" onClick={handleBack} className={mobile.secondaryBtn}>
          {backLabel}
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setActionsOpen(true)} className={mobile.secondaryBtn}>
            <MoreHorizontalIcon className="w-4 h-4" />
            Actions
          </button>
          <button
            type="button"
            onClick={() => navigate(`/contracts/edit/${contract.id}`)}
            className={mobile.primaryBtn}
          >
            <EditIcon className="w-4 h-4" />
            Edit
          </button>
        </div>
      </div>

      <MobileSheet open={actionsOpen} onClose={() => setActionsOpen(false)} title="Contract actions">
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              navigate(
                `/jobs/create?contractId=${contract.id}&returnTo=${encodeURIComponent(`/contracts/details/${contract.id}`)}`
              )
            }}
            className={`${mobile.primaryBtn} w-full !bg-purple-600 hover:!bg-purple-700`}
          >
            <BriefcaseIcon className="w-4 h-4" />
            Create Job
          </button>
          {['active', 'signed', 'in_process', 'completed', 'expired'].includes(contract.status) && (
            <button
              type="button"
              onClick={() => {
                setActionsOpen(false)
                navigate(`/contracts/create?renewContractId=${contract.id}`)
              }}
              className={`${mobile.successBtn} w-full`}
            >
              <RefreshCwIcon className="w-4 h-4" />
              Renew Contract
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              handleGeneratePdf()
            }}
            disabled={generatingPdf}
            className={`${mobile.secondaryBtn} w-full`}
          >
            <FileTextIcon className="w-4 h-4" />
            {generatingPdf ? 'Generating…' : 'Generate PDF'}
          </button>
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              navigate(`/contracts/edit/${contract.id}`)
            }}
            className={`${mobile.primaryBtn} w-full`}
          >
            <EditIcon className="w-4 h-4" />
            Edit Contract
          </button>
        </div>
      </MobileSheet>

      {/* In-app notification toast */}
      {notification && (
        <div className="fixed top-4 right-4 z-[60] max-w-md">
          <div
            className={`p-4 rounded-lg border shadow-lg ${
              notification.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : notification.type === 'error'
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-medium whitespace-pre-wrap">{notification.message}</p>
              <button
                type="button"
                onClick={() => setNotification(null)}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                aria-label="Dismiss"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Jobs date-range modal */}
      {generateJobsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !generateJobsBusy && setGenerateJobsOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Generate Jobs</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Choose the date range to create or refresh recurring jobs from visit groups.
                </p>
              </div>
              <button
                type="button"
                disabled={generateJobsBusy}
                onClick={() => setGenerateJobsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                <input
                  type="date"
                  value={generateJobsStart}
                  onChange={(e) => {
                    setGenerateJobsStart(e.target.value)
                    setGenerateJobsPreview(null)
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Through</label>
                <input
                  type="date"
                  value={generateJobsEnd}
                  onChange={(e) => {
                    setGenerateJobsEnd(e.target.value)
                    setGenerateJobsPreview(null)
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {generateJobsPreview && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-900">
                  <p className="font-medium">
                    Preview: {generateJobsPreview.count} job slot
                    {generateJobsPreview.count === 1 ? '' : 's'} in range
                  </p>
                  <p className="text-blue-800 mt-1 text-xs leading-relaxed">
                    {generateJobsPreview.summary}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                disabled={generateJobsBusy}
                onClick={() => setGenerateJobsOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={generateJobsBusy || !generateJobsStart || !generateJobsEnd}
                onClick={runGenerateJobsPreview}
                className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
              >
                {generateJobsBusy && !generateJobsPreview ? 'Previewing…' : 'Preview'}
              </button>
              <button
                type="button"
                disabled={generateJobsBusy || !generateJobsStart || !generateJobsEnd}
                onClick={confirmGenerateJobs}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {generateJobsBusy ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Template Editor Modal */}
      {id && (
        <JobTemplateEditor
          isOpen={templateEditorOpen}
          onClose={() => {
            setTemplateEditorOpen(false)
            setEditingTemplate(null)
            setNewTemplateDate(undefined)
          }}
          contractId={id}
          template={editingTemplate}
          scheduledDate={newTemplateDate}
          onSave={async () => {
            const targetDateStr = editingTemplate?.scheduled_date || newTemplateDate
            if (targetDateStr && id) {
              try {
                const targetDate = new Date(targetDateStr)
                const { generateJobsForContract } = await import('../api/jobGeneration')
                await generateJobsForContract(id, targetDate, targetDate)
              } catch (error) {
                console.error('Error creating specific dated job:', error)
              }
            }
            await fetchJobTemplates(id)
            await fetchContractJobs(id)
          }}
        />
      )}
    </MobilePage>
  )
}
